import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { READER_BLOCK_LABEL } from '@/components/reader';
import { Callout } from '@/components/ui/callout';
import type { BlockNamer, ExperiencePlace, VariableNamer } from '@/lib/experience-labels';
import {
  analystLevelsRefusalSentence,
  analystReadingRefusalSentence,
  blockerSentence,
  countLabel,
  orphanDirectionRefusalSentence,
  problemPlace,
  problemSentence,
  warningSentence,
  warningsBesideBlockers,
} from '@/lib/experience-labels';
import type { ProtocolEditorView } from '@/lib/protocol-search';
import type {
  ExperiencePageDraft,
  ExperienceProblem,
  ExperienceProductionBlocker,
  ExperienceWarning,
} from '@matter_extractor/shared';
import type { ReactNode } from 'react';

/**
 * Les deux encarts de la boîte d'enregistrement : les refus en rouge, les
 * avertissements en jaune (refonte modulaire §9.1 ; maquette
 * `EnregistrerVersion.dc.html`).
 *
 * The mockup fixes the shape: « les refus et les avertissements sont deux
 * encarts distincts, rouge puis jaune, chacun avec un titre qui compte ; chaque
 * refus se termine par un lien "Ouvrir" aligné à droite, dans la couleur de
 * l'encart ; le bouton "Enregistrer la v6" est inactif tant qu'un refus est
 * là ».
 *
 * ## Two encarts because they are two ANSWERS, not two severities
 *
 * A refusal is a version that will not be written; a warning is a version that
 * WILL be written and will not resolve in production yet (§9.1). Folded into
 * one list they would read as degrees of the same thing, and the yellow one
 * would then look like something to fix before clicking - which is exactly what
 * §9.1 says it is not: « la version se joue en développement, et c'est le
 * référent qui dit ce qu'il reste à faire ».
 *
 * ## One encart, two boxes: the title says what each refuses (issue #159)
 *
 * « Passer en production » (`renderPointerBlockers` of `experience-editor.tsx`)
 * draws this very component, and it does not refuse the same thing: the save
 * box refuses a version that will not be WRITTEN, the pointer box a pointer
 * that will not MOVE onto a version that exists already. So the red title
 * names the gesture (`gesture`), in the words of §4 and §9.1 of the refonte,
 * where « Passer en production » is the gesture that refuses: « La version ne
 * peut pas être enregistrée » in the one, « Passer en production est refusé »
 * in the other.
 *
 * And under a title that names the gesture, no line names it again. The line
 * of a protocol fact - a level or a reading missing, a direction orphaned - opens on
 * « Passer en production est refusé » in the save box, because that title
 * names the version and the fact refuses the TICK only; in the pointer box it
 * names the protocol alone - `blockerSentence`, the wording of every other
 * line there (`refusedFactSentence`).
 *
 * ## « Ouvrir » is a callback and not a `<Link>`
 *
 * This box is mounted by the versioned layer, which knows nothing of the route
 * it sits on - so the place travels as a `{ tab, pageId }` and the editor turns
 * it into `?tab=...&page=...`. The same reason `renderCardLink` exists.
 *
 * Only a PROBLEM carries one: a blocker names an object of another library or
 * the protocol page as a whole, and neither is a place inside this editor - the
 * protocol page's own fix is the page editor of #58, and a reference's fix is
 * the Persona tab, which the empty-persona problem already points at.
 *
 * ## « Ouvrir le protocole », the one door OUT of this editor (issues #154 and #196)
 *
 * A protocol whose section Analyste is incomplete (analyste.md §3.6) - a level no
 * objective carries (`analyst-levels-missing`), or a reading that is empty, one
 * line per pass (`analyst-reading-missing`) - is fixed in that protocol's editor, at
 * its section (`?vue=lecture`, N47) - another screen. So each of those lines, the
 * yellow one and, box ticked, the red one, ends on « Ouvrir le protocole »,
 * right-aligned in the colour of their encart (maquette `LectureBoiteExperience`). The
 * LINK is the caller's to build (`renderProtocolLink`), for the reason above: this
 * component knows no route.
 *
 * Since issue #158 a second fact takes the same door: a DIRECTION of the
 * protocol that cites an objective its version no longer carries (§8.6 of the
 * refonte, maquette `LectureVariablesBoiteExperience`), one line per step. It is
 * fixed in the conduite of the interview, the editor's view by default - the
 * selected step does not travel in the URL, so the line names it instead.
 *
 * Ticked, a fact is said ONCE - the yellow line gives way to the red one
 * (`warningsBesideBlockers`).
 */

/** What `renderProtocolLink` is asked for: whose editor, which view of it, and in which colour. */
export interface ProtocolLinkRequest {
  readonly protocolId: string;
  /** The view of the protocol's editor where the fact is fixed (N47): its reading, or the conduite. */
  readonly view: ProtocolEditorView;
  /** The link's classes, in the colour of the encart it ends. */
  readonly className: string;
}

/** The facts a protocol's editor fixes, whose lines end on « Ouvrir le protocole ». */
type ProtocolFact = Extract<
  ExperienceWarning,
  | { kind: 'analyst-levels-missing' }
  | { kind: 'analyst-reading-missing' }
  | { kind: 'protocol-variable-orphan' }
>;

function isProtocolFact(
  fact: ExperienceWarning | ExperienceProductionBlocker,
): fact is ProtocolFact {
  return (
    fact.kind === 'analyst-levels-missing' ||
    fact.kind === 'analyst-reading-missing' ||
    fact.kind === 'protocol-variable-orphan'
  );
}

/** Where the fact is fixed: the section Analyste (§3.6, N47), or the conduite (§8.6). */
function viewOf(fact: ProtocolFact): ProtocolEditorView {
  return fact.kind === 'protocol-variable-orphan' ? 'conduite' : 'lecture';
}

/**
 * A key per line: a protocol says its levels once, each reading once per pass (#196),
 * an orphan direction once per step.
 */
function factKey(fact: ProtocolFact): string {
  switch (fact.kind) {
    case 'protocol-variable-orphan':
      return `${fact.kind}-${fact.id}-${fact.stepId}`;
    case 'analyst-reading-missing':
      return `${fact.kind}-${fact.id}-${fact.pass}`;
    case 'analyst-levels-missing':
      return `${fact.kind}-${fact.id}`;
  }
}

/**
 * The red line of a protocol fact (§3.7, §8.6 of the refonte). The save box's
 * title names the version, so its line opens on the gesture the tick adds;
 * under the title of « Passer en production », which names the gesture, it
 * names the protocol alone, like every other line there (issue #159).
 */
function refusedFactSentence(fact: ProtocolFact, gesture: 'save' | 'production'): string {
  if (gesture === 'production') return blockerSentence(fact);
  switch (fact.kind) {
    case 'analyst-levels-missing':
      return analystLevelsRefusalSentence(fact);
    case 'analyst-reading-missing':
      return analystReadingRefusalSentence(fact);
    case 'protocol-variable-orphan':
      return orphanDirectionRefusalSentence(fact);
  }
}

export interface ExperienceSaveBlockersProps {
  problems: readonly ExperienceProblem[];
  blockers: readonly ExperienceProductionBlocker[];
  warnings: readonly ExperienceWarning[];
  /**
   * Les avertissements de contraste de §9.4, déjà rédigés (`experience-theme.ts`).
   *
   * They come in as SENTENCES and not as pairs, because the bar of §9.4 shows
   * the very same ones: one wording function, two surfaces, and no way for the
   * box to warn about a pair the bar does not. They ride the yellow encart
   * beside the server's warnings because they are the same kind of thing - a
   * version that saves and plays, with something to know about it.
   */
  contrast: readonly string[];
  /**
   * What a variable is NAMED, so a refused line reads « @email est cité page 1,
   * mais saisi page 4 » (D3). Absent, the line falls back on the stored token.
   */
  nameOf?: VariableNamer | undefined;
  /**
   * The pages being saved, so a refused display condition names its block by
   * its type - « La condition du bloc Image, page 5 » (§9.2, maquette
   * `EnregistrerVersion`). Absent, the line says « d'un bloc ».
   */
  pages?: readonly ExperiencePageDraft[] | undefined;
  /** Where a refused line leads - the editor writes the search params. */
  onOpen: (place: ExperiencePlace) => void;
  /**
   * « Ouvrir le protocole » under a protocol fact: the link to that protocol's
   * editor, at the view the request names - its reading for a protocol with no
   * reading (noyau.md §3.7), its conduite for a direction that cites an
   * objective gone (§8.6 of the refonte, #158) - built by the caller, who
   * knows the routes. Absent, the lines say the fact without the door.
   */
  renderProtocolLink?: ((request: ProtocolLinkRequest) => ReactNode) | undefined;
  /**
   * The gesture the box confirms, which the red title names (issue #159):
   * « Enregistrer une nouvelle version », the default, or « Passer en
   * production ». Under the second, the line of a protocol fact names the
   * protocol alone - the title has said the gesture.
   */
  gesture?: 'save' | 'production' | undefined;
}

/** The link's own classes, per encart: underlined, in the encart's ink (maquette). */
const LINK_CLASS = {
  warn: 'ml-auto shrink-0 font-semibold text-gray-900 text-sm underline underline-offset-2',
  danger: 'ml-auto shrink-0 font-semibold text-rouge-deep text-sm underline underline-offset-2',
} as const;

/** What the red title says is refused, per box (issue #159): the version, or the gesture. */
const REFUSED = {
  save: 'La version ne peut pas être enregistrée',
  production: 'Passer en production est refusé',
} as const;

export function ExperienceSaveBlockers({
  problems,
  blockers,
  warnings: served,
  contrast,
  nameOf,
  pages,
  onOpen,
  renderProtocolLink,
  gesture = 'save',
}: ExperienceSaveBlockersProps) {
  // Ticked, a protocol fact is refused in red, and said once (§3.7, §8.6).
  const warnings = warningsBesideBlockers(served, blockers);
  const refusals = problems.length + blockers.length;
  const blockName: BlockNamer | undefined =
    pages === undefined
      ? undefined
      : (blockId) => {
          const found = pages
            .flatMap((page) => page.temps.flatMap((temps) => temps.blocks))
            .find((block) => block.id === blockId);
          return found === undefined ? null : READER_BLOCK_LABEL[found.type];
        };
  const advisories = warnings.length + contrast.length;

  return (
    <div className="space-y-3">
      {refusals === 0 ? null : (
        <Callout
          tone="danger"
          data-testid={EXPERIENCE_IDS.refusals}
          title={`${REFUSED[gesture]} : ${countLabel(refusals, 'problème')}.`}
        >
          <ul className="space-y-1.5">
            {problems.map((problem, index) => (
              <li
                /* The sentence is no key: two blocks conditioned on the same
                   variable of one page read the same refusal word for word. */
                key={`${problem.kind}-${'blockId' in problem ? problem.blockId : ''}-${index}`}
                className="flex flex-wrap items-baseline justify-between gap-2"
              >
                <span className="min-w-0">{problemSentence(problem, nameOf, blockName)}</span>
                <button
                  type="button"
                  data-testid={EXPERIENCE_IDS.refusalOpen}
                  onClick={() => onOpen(problemPlace(problem))}
                  className="shrink-0 cursor-pointer text-rouge-deep text-sm underline underline-offset-2"
                >
                  Ouvrir
                </button>
              </li>
            ))}
            {blockers.map((blocker, index) =>
              isProtocolFact(blocker) ? (
                <li
                  key={factKey(blocker)}
                  data-testid={EXPERIENCE_IDS.refusal}
                  data-kind={blocker.kind}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-1"
                >
                  <span className="min-w-0">{refusedFactSentence(blocker, gesture)}.</span>
                  {renderProtocolLink?.({
                    protocolId: blocker.id,
                    view: viewOf(blocker),
                    className: LINK_CLASS.danger,
                  })}
                </li>
              ) : (
                <li
                  key={`${blocker.kind}-${index}`}
                  data-testid={EXPERIENCE_IDS.refusal}
                  data-kind={blocker.kind}
                >
                  {blockerSentence(blocker)}
                </li>
              ),
            )}
          </ul>
        </Callout>
      )}

      {advisories === 0 ? null : (
        <Callout
          tone="warn"
          data-testid={EXPERIENCE_IDS.warnings}
          title={`${countLabel(advisories, 'avertissement')}, jamais un refus.`}
        >
          <ul className="space-y-1.5">
            {warnings.map((warning) =>
              isProtocolFact(warning) ? (
                <li
                  key={factKey(warning)}
                  data-testid={EXPERIENCE_IDS.warning}
                  data-kind={warning.kind}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-1"
                >
                  <span className="min-w-0">{warningSentence(warning)}.</span>
                  {renderProtocolLink?.({
                    protocolId: warning.id,
                    view: viewOf(warning),
                    className: LINK_CLASS.warn,
                  })}
                </li>
              ) : (
                <li
                  key={`${warning.kind}-${warningSentence(warning)}`}
                  data-testid={EXPERIENCE_IDS.warning}
                  data-kind={warning.kind}
                >
                  {warningSentence(warning)}
                </li>
              ),
            )}
            {contrast.map((sentence) => (
              <li key={sentence}>{sentence}</li>
            ))}
          </ul>
        </Callout>
      )}
    </div>
  );
}
